<script setup lang="ts">
import {bannerPath} from "../../../utils/bannerSlug";
import getTeamName from "../../../utils/teamName";
import {computed, ref, watch} from "vue";

const props: any = defineProps(["design"]);

const imageSrc = computed(() => props.design?.img_url || props.design?.jpg_url || "");
const isImageLoading = ref(true);

watch(
  imageSrc,
  () => {
    isImageLoading.value = !!imageSrc.value;
  },
  {immediate: true}
);

const onImageLoaded = () => {
  isImageLoading.value = false;
};
</script>

<template>
    <div c2c-0 class="design-card" v-if="design">
        <a c2c-1 class="design-thumb-link" :href="bannerPath(design.label, design._id)">
            <div c2c-2 class="thumb-skeleton" v-if="isImageLoading"></div>
            <img
              c2c-3 class="design-thumb"
              :class="{hidden: isImageLoading}"
              :src="imageSrc"
              :alt="design.alt"
              @load="onImageLoaded"
              @error="onImageLoaded"
            >
        </a>

        <div c2c-4 class="design-body">
            <p c2c-5 class="price-line">
                <span c2c-6 class="price-label">Price:</span>
                <span c2c-7 class="price-sale">
                    ${{ design.type === 'triangle' || design.type === 'home-plate' ? '9.99' : design.sale_price }}
                </span>
                <span c2c-8 class="price-origin">${{ design.price }}</span>
            </p>

            <p c2c-9 class="shipping-line">Fast Shipping: <span>1–3 Days</span></p>

            <p c2c-10 class="tag-line">
                <span>Tags:</span>
                <a :href="`/search/?q=${getTeamName(design.label)}`">{{ getTeamName(design.label) }}</a>
            </p>

            <div c2c-11 class="action-wrap">
                <a c2c-12 class="btn btn-live" :href="`/design-tool/?u=${design.svg_url}`">
                    <img height="18" src="/icon/paint.webp" alt="icon paint">
                    <span>Live Design</span>
                </a>

                <a c2c-13 class="btn btn-order" :href="bannerPath(design.label, design._id)">
                    <span c2c-14 class="icon-pencil"></span>
                    <span>Order Form</span>
                </a>
            </div>

            <p c2c-15 class="stat-line">Views: {{ design.views }} / Sold: {{ design.num_purchases }}</p>
        </div>
    </div>
</template>

<style scoped>
.design-card {
    padding: 12px;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.design-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
}

.design-thumb-link {
    display: block;
    position: relative;
}

.thumb-skeleton {
    width: 100%;
    border-radius: 10px;
    aspect-ratio: 5 / 3;
    background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
    background-size: 220% 100%;
    animation: sk 1.25s ease-in-out infinite;
}

.design-thumb {
    width: 100%;
    display: block;
    border-radius: 10px;
    object-fit: cover;
    aspect-ratio: 5 / 3;
}

.design-thumb.hidden {
    display: none;
}

@keyframes sk {
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: -100% 0;
    }
}

.design-body {
    margin-top: 12px;
}

.price-line {
    margin: 0;
    text-align: center;
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 10px;
}

.price-label {
    color: #334155;
    font-weight: 500;
}

.price-sale {
    color: #dc2626;
    font-size: 28px;
    font-weight: 800;
    line-height: 1;
}

.price-origin {
    color: #64748b;
    text-decoration: line-through;
    font-size: 18px;
    font-weight: 700;
}

.shipping-line,
.tag-line,
.stat-line {
    margin: 10px 0 0;
    text-align: center;
    color: #475569;
    font-size: 14px;
}

.shipping-line span {
    color: #0f172a;
    font-weight: 700;
}

.tag-line span {
    font-weight: 700;
}

.tag-line a {
    margin-left: 6px;
    color: #0f766e;
    text-decoration: underline;
}

.tag-line a:hover {
    color: #115e59;
}

.action-wrap {
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.btn {
    width: min(100%, 190px);
    min-height: 38px;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid #cbd5e1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    text-decoration: none;
    transition: all 0.2s ease;
}

.btn-live {
    color: #0f172a;
    background: #ffffff;
}

.btn-live:hover {
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.16);
}

.btn-order {
    color: #ffffff;
    border-color: #0f766e;
    background: linear-gradient(90deg, #0f766e 0%, #0d9488 100%);
}

.btn-order:hover {
    filter: brightness(1.03);
    box-shadow: 0 8px 18px rgba(15, 118, 110, 0.28);
}

.stat-line {
    margin-top: 10px;
    font-size: 12px;
    color: #64748b;
}

@media (max-width: 640px) {
    .price-sale {
        font-size: 24px;
    }

    .price-origin {
        font-size: 16px;
    }
}
</style>
<style lang="scss" scoped></style>